import _ from "lodash"
// import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import incrementUnique from 'data/lib/increment-unique'
import { diff_obj } from 'data/lib/diff'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"
import propertyService from "./property"

export type MarketLease = {
  propertyid: number
  marketleaseid: number
}
// private module logic //

const id: string = "marketlease"
const initialState: ApiResult<MarketLease[]> = { id, state: "init", value: [] }
const marketLeaseStream = new BehaviorSubject<ApiResult<MarketLease[]>>(initialState)
const marketLeaseEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let marketLeaseMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  marketLeaseStream.next({ id, state: 'ok', value: [] })
  marketLeaseMap = {}
}

function get(marketleaseid: number): MarketLease {
  return marketLeaseMap[marketleaseid]
}

function refresh(): Promise<any> {
  const currentValue = marketLeaseStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchMarketLeases(propertyid)
}

function copy(propertyid:number, marketleaseid:number, code:string) {
  const currentMarketLeaseVal = marketLeaseStream.getValue().value;
  marketLeaseStream.next({ id, state: 'busy', value: currentMarketLeaseVal });

  const payload = {table: 'marketlease', recordid: marketleaseid, _synchronous: true, code: incrementUnique(code, "code", currentMarketLeaseVal)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((marketLease: MarketLease) => {
      const newRecord = marketLease[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.marketleaseid),
        redofunc: () => copy(propertyid, marketleaseid, code)
      });
      marketLeaseMap[newRecord.marketleaseid] = newRecord
      // merge results unique by marketleaseid
      const currentMarketLeaseVal = marketLeaseStream.getValue().value
      marketLeaseStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentMarketLeaseVal, 'marketleaseid').sort((a, b) => alphanum(a.code, b.code))
      })
      marketLeaseEventStream.next({ type: 'MARKET_LEASE_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      marketLeaseStream.next({ id, state: 'error', error: err, value: currentMarketLeaseVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchMarketLeases(propertyid)
      return err
    })
}

function copies(propertyid:number, marketleaseid:number, count: number) {
  const currentMarketLeaseVal = marketLeaseStream.getValue().value;
  marketLeaseStream.next({ id, state: 'busy', value: currentMarketLeaseVal });

  const payload = {table: 'marketlease', recordid: marketleaseid, _synchronous: true, unique: true, count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((marketleases: MarketLease[]) => {
      push_undo({
        undofunc: () => deleteMultiple(propertyid, _.map(marketleases, 'marketleaseid')),
        redofunc: () => copies(propertyid, marketleaseid, count)
      });
      _.forEach(marketleases, newRecord => marketLeaseMap[newRecord.marketleaseid] = newRecord)
      // merge results unique by marketleaseid
      const currentMarketLeaseVal = marketLeaseStream.getValue().value
      marketLeaseStream.next({
        id,
        state: 'ok',
        value: _.unionBy(marketleases, currentMarketLeaseVal, 'marketleaseid').sort((a, b) => alphanum(a.code, b.code))
      })
      marketLeaseEventStream.next({ type: 'MARKET_LEASE_COPIED', payload: marketleases })
      return marketleases
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      marketLeaseStream.next({ id, state: 'error', error: err, value: currentMarketLeaseVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchMarketLeases(propertyid)
      return err
    })
}

function create(propertyid: number): Promise<MarketLease | { message: string }> {
  const currentValue = marketLeaseStream.getValue().value
  marketLeaseStream.next({ id, state: 'busy', value: currentValue })
  const property = propertyService.get();

  const payload = {
    propertyid,
    breakpointdetail: {
      state: 'basic',
      breakPoint: property['defaultsmarketlease']['breakPoint']
    },
    downtimedetail: {
      state: 'basic',
      downtime: property['defaultsmarketlease']['downtime']
    },
    overagedetail: {
      state: 'inherit',
      overage: 'Inherit'
    },
    renewaldetail: {
      state: 'basic',
      renewalProbability: property['defaultsmarketlease']['renewalProbability']
    },
    salesdetail: {
      state: 'inherit',
      sales: 'Inherit'
    },
    termdetail: {
      state: 'basic',
      term: property['defaultsmarketlease']['term']
    }
  }

  return Client.post(`marketlease`, payload).then((marketLeases: MarketLease) => {
    push_undo({
      undofunc: () => deleteOne(propertyid, marketLeases.marketleaseid),
      redofunc: () => create(propertyid)
    })
    marketLeaseMap[marketLeases.marketleaseid] = marketLeases
    // merge results unique by marketleaseid
    const currentValue = marketLeaseStream.getValue().value
    marketLeaseStream.next({
      id,
      state: 'ok',
      value: _.unionBy([marketLeases], currentValue, 'marketleaseid')
    })
    marketLeaseEventStream.next({ type: 'MARKET_LEASE_CREATED', payload: marketLeases })
    return marketLeases
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    marketLeaseStream.next({ id, state: 'error', error: err, value: currentValue })
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    fetchMarketLeases(propertyid)
    return err
  })
}

async function deleteOne(
  propertyid: number,
  marketleaseid: number
): Promise<boolean | { message: string }> {
  const currentValue = marketLeaseStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, marketleaseid)
  const refreshFn = () => fetchMarketLeases(propertyid)
  if (!(await handleReferences('marketlease', marketleaseid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted marketleaseid
  const value = _(currentValue)
    .filter((p) => p.marketleaseid !== marketleaseid)
    .value()
  value.forEach((p) => (marketLeaseMap[p.marketleaseid] = p))

  marketLeaseStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(marketleaseid).then(() =>
  Client.del(`marketlease?marketleaseid=eq.${marketleaseid}`)
    .then((marketLeases: MarketLease) => {
      delete marketLeaseMap[marketLeases.marketleaseid]
      push_undo({
        undofunc: () => {
          Client.post('marketlease', marketLeases)
          marketLeaseMap[marketLeases.marketleaseid] = marketLeases
          marketLeaseStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, marketleaseid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      marketLeaseStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchMarketLeases(propertyid)
      return err
    })
  )
}

async function deleteMultiple(
  propertyid: number,
  marketleaseids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = marketLeaseStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchMarketLeases(propertyid)
  if (!(await handleReferences('marketlease', marketleaseids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted marketleaseid
  const value = _(currentValue)
    .filter((p) => !marketleaseids.includes(p.marketleaseid))
    .value()
  value.forEach((p) => (marketLeaseMap[p.marketleaseid] = p))

  marketLeaseStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(marketleaseids).then(() =>
  Client.del(`marketlease?marketleaseid=in.${marketleaseids}`)
    .then((marketleases: MarketLease[]) => {
      marketleases.forEach(m => delete marketLeaseMap[m.marketleaseid])
      push_undo({
        undofunc: () => {
          Client.post('marketlease', marketleases)
          marketleases.forEach(m => marketLeaseMap[m.marketleaseid] = m)
          marketLeaseStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(propertyid, marketleaseids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      marketLeaseStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchMarketLeases(propertyid)
      return err
    })
  )
}

function fetchMarketLeases(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<MarketLease[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  marketLeaseStream.next({ id, state: 'busy', value: marketLeaseStream.getValue().value })

  let url = `marketlease?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `marketlease?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: MarketLease[]) => {
    _.forEach(records, (marketLeases) => (marketLeaseMap[marketLeases.marketleaseid] = marketLeases))
    // merge results unique by marketleaseid
    marketLeaseStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function patch(marketlease: MarketLease): Promise<MarketLease | { message: string }> {
  const currentValue = marketLeaseStream.getValue().value
  const marketleaseid: number = marketlease.marketleaseid
  const currentMarketlease = _.find(currentValue, { marketleaseid })
  const changeset = diff_obj(marketlease, currentMarketlease)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentMarketlease),
    redofunc: () => patch(marketlease)
  })

  // optimistic update
  marketLeaseStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {marketleaseid}), marketlease)
  })

  return _patches_.watch(marketleaseid, Client.patch(`marketlease?marketleaseid=eq.${marketleaseid}`, changeset)
    .then((mf: MarketLease[]) => {
      _.forEach(mf, (marketlease) => (marketLeaseMap[marketlease.marketleaseid] = marketlease))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      marketLeaseStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentMarketlease, 'propertyid')
      if (propertyid) fetchMarketLeases(propertyid)
      return err
    }))
}

// public module interface //

const marketLeaseService = {
  stream: () => marketLeaseStream.asObservable(),
  value: () => marketLeaseStream.getValue().value,
  eventStream: () => marketLeaseEventStream.asObservable(),
  copy,
  copies,
  clear,
  create,
  deleteOne,
  deleteMultiple,
  get,
  fetchMarketLeases,
  patch,
  refresh
}

export default marketLeaseService
